import React, { useState } from "react";
import { supabase } from "./supabaseClient.ts";
import { ArrowLeft, CreditCard, Mail, Lock, Trash2, CheckCircle } from "lucide-react";

interface ProfilePageProps {
  user: any;
  isPremium: boolean;
  onClose: () => void;
}

const ProfilePage: React.FC<ProfilePageProps> = ({ user, isPremium, onClose }) => {
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [deleteConfirmation, setDeleteConfirmation] = useState("");

  const showMessage = (text: string, type: "success" | "error") => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: "", type: "" }), 5000);
  };

  // --- GESTION DU PORTAIL STRIPE ---
  const handleManageBilling = async () => {
    setLoadingPortal(true);
    try {
      // 1. Récupérer l'ID Stripe de l'utilisateur dans la table profiles
      const { data, error } = await supabase
        .from("profiles")
        .select("stripe_customer_id")
        .eq("id", user.id)
        .single();

      if (error || !data?.stripe_customer_id) {
        showMessage("Aucun historique de facturation trouvé.", "error");
        setLoadingPortal(false);
        return;
      }

      // 2. Appeler notre API Vercel
      const response = await fetch("/api/create-portal-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stripeCustomerId: data.stripe_customer_id }),
      });

      const { url } = await response.json();
      if (url) {
        window.location.href = url; // Redirection magique vers Stripe
      } else {
        showMessage("Erreur lors de la création du portail.", "error");
      }
    } catch (err) {
      showMessage("Erreur de connexion au serveur.", "error");
    }
    setLoadingPortal(false);
  };

  // --- MISE À JOUR DE L'EMAIL ---
  const handleUpdateEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail) return;
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    if (error) {
      showMessage(error.message, "error");
    } else {
      showMessage("Un lien de confirmation a été envoyé à votre nouvelle adresse.", "success");
      setNewEmail("");
    }
  };

  // --- MISE À JOUR DU MOT DE PASSE ---
  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showMessage("Les mots de passe ne correspondent pas.", "error");
      return;
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) {
      showMessage(error.message, "error");
    } else {
      showMessage("Mot de passe mis à jour avec succès !", "success");
      setNewPassword("");
      setConfirmPassword("");
    }
  };

  // --- SUPPRESSION DU COMPTE ---
  const handleDeleteAccount = async () => {
    // Sécurité supplémentaire : on revérifie ici au cas où
    if (deleteConfirmation !== user?.email) return;

    try {
      showMessage("Suppression en cours, veuillez patienter...", "success"); 
      
      const response = await fetch("/api/delete-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: user.id }),
      });

      if (response.ok) {
        alert("Votre compte et toutes vos données ont été définitivement supprimés.");
        await supabase.auth.signOut();
        window.location.reload(); // Recharge la page pour tout remettre à zéro
      } else {
        showMessage("Une erreur est survenue lors de la suppression.", "error");
      }
    } catch (err) {
      showMessage("Erreur de connexion au serveur.", "error");
    }
  };

  const sectionStyle = {
    backgroundColor: "white",
    padding: "2rem",
    borderRadius: "1rem",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)",
    marginBottom: "2rem",
    border: "1px solid #e5e7eb"
  };

  const inputStyle = {
    width: "100%", padding: "0.75rem", borderRadius: "0.5rem", border: "1px solid #d1d5db", marginTop: "0.5rem", marginBottom: "1rem"
  };

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto", padding: "2rem 1rem", fontFamily: "sans-serif" }}>
      {/* HEADER */}
      <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: "0.5rem", background: "none", border: "none", color: "#6b7280", cursor: "pointer", marginBottom: "2rem", fontSize: "1rem" }}>
        <ArrowLeft size={20} /> Retour au tableau de bord
      </button>

      <h1 style={{ fontSize: "2rem", fontWeight: "bold", color: "#1f2937", marginBottom: "2rem" }}>Paramètres du compte</h1>

      {/* NOUVEL AFFICHAGE DES MESSAGES (TOAST FLOTTANT) */}
      {message.text && (
        <div style={{
          position: "fixed",
          bottom: "2rem",
          right: "2rem",
          zIndex: 9999,
          padding: "1rem 1.5rem",
          borderRadius: "0.5rem",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          backgroundColor: message.type === "success" ? "#d1fae5" : "#fee2e2",
          color: message.type === "success" ? "#065f46" : "#991b1b",
          boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
          maxWidth: "90vw",
          border: message.type === "success" ? "1px solid #10b981" : "1px solid #ef4444"
        }}>
          <CheckCircle size={20} />
          <span style={{ fontWeight: "500", fontSize: "0.95rem" }}>{message.text}</span>
        </div>
      )}

      {/* SECTION 1 & 2 : ABONNEMENT ET FACTURES */}
      <section style={sectionStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
          <CreditCard size={24} color="#2563eb" />
          <h2 style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#1f2937", margin: 0 }}>Abonnement & Factures</h2>
        </div>
        <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>Gérez votre forfait, téléchargez vos factures et mettez à jour votre moyen de paiement.</p>
        
        <div style={{ backgroundColor: isPremium ? "#f0fdf4" : "#f3f4f6", padding: "1.5rem", borderRadius: "0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", border: isPremium ? "1px solid #bbf7d0" : "1px solid #e5e7eb" }}>
          <div>
            <p style={{ fontWeight: "bold", color: isPremium ? "#166534" : "#374151", margin: "0 0 0.25rem 0" }}>
              {isPremium ? "Abonnement Premium Actif" : "Version Gratuite"}
            </p>
            {isPremium && <p style={{ margin: 0, fontSize: "0.875rem", color: "#15803d" }}>Merci de votre confiance.</p>}
          </div>
          
          <button 
            onClick={isPremium ? handleManageBilling : undefined}
            disabled={loadingPortal}
            style={{ backgroundColor: "#1f2937", color: "white", padding: "0.75rem 1.5rem", borderRadius: "0.5rem", border: "none", cursor: isPremium ? "pointer" : "not-allowed", fontWeight: "500", opacity: isPremium ? 1 : 0.5 }}
          >
            {loadingPortal ? "Chargement..." : "Gérer mon abonnement (Portail Sécurisé)"}
          </button>
        </div>
      </section>

      {/* SECTION 3 : EMAIL */}
      <section style={sectionStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
          <Mail size={24} color="#2563eb" />
          <h2 style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#1f2937", margin: 0 }}>Adresse email</h2>
        </div>
        <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>Email actuel : <strong>{user?.email}</strong></p>
        <form onSubmit={handleUpdateEmail}>
          <label style={{ fontSize: "0.875rem", fontWeight: "500", color: "#374151" }}>Nouvelle adresse email</label>
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="nouvelle@email.com" required style={inputStyle} />
          <button type="submit" style={{ backgroundColor: "#2563eb", color: "white", padding: "0.75rem 1.5rem", borderRadius: "0.5rem", border: "none", cursor: "pointer", fontWeight: "500" }}>
            Modifier l'email
          </button>
        </form>
      </section>

      {/* SECTION 4 : MOT DE PASSE */}
      <section style={sectionStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
          <Lock size={24} color="#2563eb" />
          <h2 style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#1f2937", margin: 0 }}>Mot de passe</h2>
        </div>
        <p style={{ color: "#6b7280", marginBottom: "1.5rem" }}>Modifiez votre mot de passe pour sécuriser votre compte.</p>
        <form onSubmit={handleUpdatePassword}>
          <label style={{ fontSize: "0.875rem", fontWeight: "500", color: "#374151" }}>Nouveau mot de passe</label>
          <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} placeholder="••••••••" required style={inputStyle} minLength={6} />
          <label style={{ fontSize: "0.875rem", fontWeight: "500", color: "#374151" }}>Confirmer le nouveau mot de passe</label>
          <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" required style={inputStyle} minLength={6} />
          <button type="submit" style={{ backgroundColor: "#2563eb", color: "white", padding: "0.75rem 1.5rem", borderRadius: "0.5rem", border: "none", cursor: "pointer", fontWeight: "500" }}>
            Modifier le mot de passe
          </button>
        </form>
      </section>

      {/* SECTION 5 : ZONE DE DANGER */}
      <section style={{ ...sectionStyle, border: "1px solid #fca5a5", backgroundColor: "#fef2f2" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
          <Trash2 size={24} color="#dc2626" />
          <h2 style={{ fontSize: "1.25rem", fontWeight: "bold", color: "#dc2626", margin: 0 }}>Zone de danger</h2>
        </div>
        <p style={{ color: "#991b1b", marginBottom: "1.5rem", lineHeight: "1.5" }}>
          <strong>⚠️ Attention :</strong> La suppression de votre compte effacera toutes vos données et crédits de manière irréversible. 
          <br /><br />
          <strong>Cela ne résilie pas automatiquement votre abonnement Premium.</strong> Si vous avez un abonnement en cours, veuillez d'abord l'annuler en cliquant sur le bouton "Gérer mon abonnement" dans la section ci-dessus avant de supprimer votre compte.
        </p>

        <div style={{ backgroundColor: "white", padding: "1rem", borderRadius: "0.5rem", border: "1px solid #fca5a5", marginBottom: "1rem" }}>
          <label style={{ fontSize: "0.875rem", fontWeight: "500", color: "#374151", display: "block", marginBottom: "0.5rem" }}>
            Pour confirmer, veuillez taper votre adresse email (<strong>{user?.email}</strong>) :
          </label>
          <input 
            type="email" 
            value={deleteConfirmation} 
            onChange={(e) => setDeleteConfirmation(e.target.value)} 
            placeholder={user?.email} 
            style={inputStyle} 
            /*onPaste={(e) => e.preventDefault()} /* Optionnel : empêche le copier-coller pour forcer la saisie manuelle */
          />
        </div>

        <button 
          onClick={handleDeleteAccount} 
          disabled={deleteConfirmation !== user?.email}
          style={{ 
            backgroundColor: "#dc2626", 
            color: "white", 
            padding: "0.75rem 1.5rem", 
            borderRadius: "0.5rem", 
            border: "none", 
            cursor: deleteConfirmation === user?.email ? "pointer" : "not-allowed", 
            fontWeight: "500",
            opacity: deleteConfirmation === user?.email ? 1 : 0.5,
            transition: "opacity 0.2s"
          }}
        >
          Supprimer définitivement mon compte
        </button>
      </section>

    </div>
  );
};

export default ProfilePage;
